import React, { ReactNode, useEffect, useRef, useState } from "react";

import { Dropdown, Menu } from "antd";

import { DropDownProps } from "antd/lib/dropdown";

import Icon from "shared/components/Icon";
import Tooltip from "shared/components/Tooltip";

import { subscribeCloseOnScroll } from "./dropdownScrollParents";
import { DropdownMenuItem } from "./interfaces";
import { DropdownLink, MenuItem } from "./styled";

const itemClick = (allItems: DropdownMenuItem[], key: number | string) => {
  const clickedItem = allItems.filter((item) => item.key === key)[0];
  clickedItem.onClick();
};

const withTooltip = ({ tooltip, label }) => {
  if (tooltip) {
    return <Tooltip {...tooltip}>{label}</Tooltip>;
  }

  return label;
};

interface DropdownMenuProps extends Partial<DropDownProps> {
  children?: ReactNode;
  items: DropdownMenuItem[];
  light?: boolean;
}

const DropdownMenu = ({
  children = <Icon.HoverTracker Icon={Icon.DropdownIcon} />,
  items,
  placement = "bottomLeft",
  onVisibleChange,
  light = false,
  align,
  disabled,
  ...props
}: DropdownMenuProps) => {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleVisibleChange = (nextVisible: boolean) => {
    setVisible(nextVisible);
    onVisibleChange?.(nextVisible);
  };

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    return subscribeCloseOnScroll(triggerRef.current, () => {
      handleVisibleChange(false);
    });
  }, [visible]);

  return (
    <Dropdown
      placement={placement}
      visible={visible}
      overlay={
        <Menu
          className={light ? "light-dropdown" : ""}
          onClick={({ key }) => {
            itemClick(items, key);
            handleVisibleChange(false);
          }}
        >
          {items.map(({ key, label, color, ltMdOnly, disabled: itemDisabled, tooltip }) => (
            <MenuItem
              key={key}
              color={color}
              ltMdOnly={ltMdOnly}
              disabled={itemDisabled}
              data-testid={key}
            >
              {withTooltip({ label, tooltip })}
            </MenuItem>
          ))}
        </Menu>
      }
      trigger={disabled ? [] : ["click"]}
      onVisibleChange={handleVisibleChange}
      align={align}
    >
      <DropdownLink
        data-testid="dropdown-menu"
        className="ant-dropdown-link"
        style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
        {...props}
      >
        <span ref={triggerRef}>{children}</span>
      </DropdownLink>
    </Dropdown>
  );
};

export default DropdownMenu;
